import { storeLansOf } from './storeOptions.js'

/**
 * **할인 화면의 매장 원천 — 한 컴포넌트, 두 라우트.** 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지 — 같은
 * 성질의 `storeOptions` 만 상대 경로로 읽는다).
 *
 * 할인 관리 화면(`DiscountListForStoreView`)은 매장 계정의 것이었다 — 매장이 **세션**이고(`/discount/store`),
 * 언어 축은 `GET /store/getStore` 가 준다. 사업장 관리자의 「할인 관리」(`/v/business/discount/list`)는 메뉴 행만
 * 있고 라우트가 없던 죽은 메뉴였고, 관리점·운영사(R-E2)가 같은 메뉴를 받으면서 채운다. 그 계정들에게는 세션
 * 매장이 없다(`/store/getStore` 는 빈 200) — 매장을 **고르고**(`StorePicker`) 그 `storeCd` 를 쿼리·본문으로 싣는다.
 *
 * 두 화면은 폼·검증·한도 칸(R-B)·표가 같고 **매장을 어디서 얻는가**만 다르다. 그래서 화면을 복제하지 않고 원천
 * 하나를 고른다: 경로·액션·질의·등록 본문의 차이가 전부 이 표에 있다. 서버 계약:
 * `R-E2-관리점운영대행-appserver-report.md` §5-1(`/discount?storeCd=` · `POST /discount` 본문 `store` · `/discount/{cd}`).
 */

/** 원천 이름 — 라우트가 컴포넌트 prop 으로 넘긴다 */
export const DISCOUNT_STORE_SOURCE = Object.freeze({
  /** 매장 계정 — 세션 매장(`/v/business/discount/list_for_store`) */
  SESSION: 'session',
  /** 사업장 관리자·관리점·운영사 — 고른 매장(`/v/business/discount/list`) */
  PICKED: 'picked',
})

/** 고른 매장으로 쓰는 할인 화면의 라우트 — `sys_menu` 의 「할인 관리」 행과 같은 값 */
export const DISCOUNT_PICKED_PATH = '/v/business/discount/list'

const SOURCES = Object.freeze({
  [DISCOUNT_STORE_SOURCE.SESSION]: Object.freeze({
    picksStore: false,
    url: '/discount/store',
    actions: Object.freeze({
      search: 'DiscountForStoreSearch',
      add: 'DiscountForStoreAdd',
      up: 'DiscountForStoreUp',
      del: 'DiscountForStoreDel',
    }),
  }),
  [DISCOUNT_STORE_SOURCE.PICKED]: Object.freeze({
    picksStore: true,
    url: '/discount',
    actions: Object.freeze({
      search: 'DiscountSearch',
      add: 'DiscountAdd',
      up: 'DiscountUp',
      del: 'DiscountDel',
    }),
  }),
})

/**
 * 원천 이름 → 원천. 모르는 이름은 **세션 원천**이다(종전 화면의 동작 — 라우트가 prop 을 안 주면 그대로).
 *
 * @param {?string} name [DISCOUNT_STORE_SOURCE]
 * @returns {{picksStore: boolean, url: string, actions: {search: string, add: string, up: string, del: string}}}
 */
export function discountStoreSource(name) {
  return SOURCES[name] || SOURCES[DISCOUNT_STORE_SOURCE.SESSION]
}

/**
 * 그 매장으로 여는 할인 화면(고른 매장 원천) — 화면이 쿼리 `storeCd` 로 그 매장에서 시작한다(쿼리 값은 문자열).
 *
 * @param {number|string} storeCd
 * @returns {{path: string, query: {storeCd: string}}}
 */
export function pickedDiscountRoute(storeCd) {
  return { path: DISCOUNT_PICKED_PATH, query: { storeCd: String(storeCd) } }
}

/**
 * 목록 질의 — 고른 매장 원천만 `storeCd` 를 싣는다(세션 원천은 서버가 세션 매장을 쓴다). 새 객체다.
 *
 * @param {object} source [discountStoreSource] 결과
 * @param {object} searchForm 화면의 검색 조건
 * @param {?number} storeCd 고른 매장
 * @returns {object}
 */
export function discountListQuery(source, searchForm, storeCd) {
  const base = Object.assign({}, searchForm || {})

  return source && source.picksStore ? Object.assign(base, { storeCd }) : base
}

/**
 * 등록 본문 — 고른 매장 원천만 `store` 를 싣는다(서버 `POST /discount` 가 그 매장의 범위를 본다). 새 객체다.
 * 수정은 싣지 않는다: 서버가 매장 축을 **현재 소유 매장**으로 정한다(본문 `store` 무시).
 *
 * @param {object} source [discountStoreSource] 결과
 * @param {object} payload `discountSavePayload` 결과
 * @param {?number} storeCd 고른 매장
 * @returns {object}
 */
export function discountAddBody(source, payload, storeCd) {
  const base = Object.assign({}, payload || {})

  return source && source.picksStore ? Object.assign(base, { store: { storeCd } }) : base
}

/**
 * **확정된 매장 컨텍스트** — 목록·등록의 대상 매장과 폼의 언어 축·국제결제를 **한 값**으로 묶는다(얼린 새 객체).
 *
 * 할인 화면에서 「어느 매장인가」와 「그 매장의 언어 설정이 무엇인가」가 따로 살면, 둘이 어긋난 구간(고른 매장의
 * 메타데이터를 기다리는 중)에 목록은 새 매장인데 폼은 앞 매장의 언어 축으로 서서 **다른 매장의 설정으로 저장**된다
 * (R-G 2차 교차 리뷰). 그래서 둘을 메타데이터가 도착한 순간 **함께** 세우고, 전환이 시작되면 함께 비운다.
 *
 * 세션 매장(`GET /store/getStore`)과 매장 셀렉트 행(`GET /store/combo`)은 같은 칸(`defaultLan`·`lans`·
 * `internationalPayYn`)을 준다. 대상 매장 코드는 **고른 원천만** 싣는다 — 세션 원천은 서버가 세션 매장을 쓴다.
 *
 * @param {object} source [discountStoreSource] 결과
 * @param {?object} store 매장 셀렉트 행 또는 세션 매장
 * @returns {{storeCd: ?number, storeLans: Array, lans: Array, internationalPayYn: boolean}}
 */
export function discountStoreContext(source, store) {
  const picked = !!(source && source.picksStore)

  return Object.freeze({
    storeCd: picked && store && store.storeCd != null ? store.storeCd : null,
    /** 폼의 언어별 행(기본 언어 제외) */
    storeLans: store && Array.isArray(store.lans) ? store.lans : [],
    /** 사전 조회 순서(기본 언어가 맨 앞) — 규칙은 [storeLansOf] 한 곳 */
    lans: storeLansOf(store),
    internationalPayYn: !!(store && store.internationalPayYn),
  })
}

/**
 * **할 수 있는 일은 확정 컨텍스트 하나에서 나온다** — 검색·등록·수정·삭제·저장(얼린 새 객체).
 *
 * 컨텍스트가 없으면(진입 직후·매장 전환 중·확정 매장 없이 실패) 대상 매장도 언어 축도 없으므로 아무것도 할 수 없다.
 * 버튼마다 잠금 플래그를 두고 자리마다 풀면, 한 자리(전환 중 검색 → 등록이 열림)가 빠지는 날 다른 매장의 설정으로
 * 저장된다. 수정·삭제는 그 위에 고른 행이 있어야 한다.
 *
 * @param {?object} context [discountStoreContext] 결과
 * @param {?object} selectedRow 수정할 행
 * @param {Array} checkedCds 삭제할 행의 할인 코드
 * @returns {{search: boolean, add: boolean, up: boolean, del: boolean, save: boolean}}
 */
export function discountActions(context, selectedRow, checkedCds) {
  const ready = context != null

  return Object.freeze({
    search: ready,
    add: ready,
    up: ready && selectedRow != null,
    del: ready && Array.isArray(checkedCds) && checkedCds.length > 0,
    save: ready,
  })
}
